import React, { useState, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import JisDialogForm from "./formdialog";
import JisYesNo from "./controls/jisYesNo";
import JisCheckbox from "./controls/jisCheckbox";
import JisButton from "./controls/jisButton";
import MultiSelectDropdown from "./controls/multiselectdropdown";
import DynamicIcon from "./dynamicicon";
import * as Constants from "../constants";
import { ShareContext } from "../context/ShareContext";
import useComments from "../hooks/useComment";
import { Autocomplete, TextField } from "@mui/material";

// Move CommentDropdown outside to prevent re-creation on every render
export const CommentDropdown = ({
  onChange,
  name,
  placeholder,
  required,
  reasonPlaceHolder,
  value = "",
  predefinedComments = [],
}) => {
  const [selectedComments, setSelectedComments] = useState([]);

  // Load predefined selections from value
  useEffect(() => {
    if (value && predefinedComments.length > 0) {
      const parts = value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);

      const predefinedSelected = parts.filter((p) =>
        predefinedComments.some((comment) =>
          typeof comment === "string" ? comment === p : comment.source === p,
        ),
      );

      setSelectedComments(predefinedSelected);
    } else {
      setSelectedComments([]);
    }
  }, [value, predefinedComments]);

  // Update parent component
  const updateParent = (finalValue) => {
    if (onChange) {
      onChange({ target: { name, value: finalValue } });
    }
  };

  // Merge and update the parent
  const updateFinal = (updatedSelected, customText) => {
    const cleanedCustom = customText.split(",").map((v) => v.trim());

    const finalValue = [...updatedSelected, ...cleanedCustom]
      .filter(Boolean)
      .join(", ");

    updateParent(finalValue);
  };

  // Handle checkbox change
  const handleCheckboxChange = (e) => {
    const { checked, value: comment } = e.target;

    const updatedSelected = checked
      ? [...selectedComments, comment]
      : selectedComments.filter((c) => c !== comment);

    setSelectedComments(updatedSelected);

    // Get current custom text (non-predefined parts)
    const currentParts = (value || "")
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);

    const customOnly = currentParts.filter(
      (p) =>
        !predefinedComments.some((comment) =>
          typeof comment === "string" ? comment === p : comment.source === p,
        ),
    );

    updateFinal(updatedSelected, customOnly.join(", "));
  };

  // Handle textarea typing
  const handleCustomChange = (e) => {
    const inputText = e.target.value;

    // Update parent immediately for responsive typing
    updateParent(inputText);

    // Update internal selected comments based on what's typed
    const parts = inputText
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);

    const predefinedInText = parts.filter((p) =>
      predefinedComments.some((comment) =>
        typeof comment === "string" ? comment === p : comment.source === p,
      ),
    );
    setSelectedComments(predefinedInText);
  };

  return (
    <div>
      {reasonPlaceHolder && (
        <p className="reason_placeholder">{reasonPlaceHolder}</p>
      )}
      <div className="predefined_comment_wrapper">
        {predefinedComments.map((comment, index) => {
          const commentText =
            typeof comment === "string" ? comment : comment.source;
          return (
            <label key={index} className="predefined_checkbox_wrapper">
              <input
                type="checkbox"
                value={commentText}
                checked={selectedComments.includes(commentText)}
                onChange={handleCheckboxChange}
              />
              <p>{commentText}</p>
            </label>
          );
        })}
      </div>

      <div>
        <label style={{ marginBottom: "120px" }} htmlFor="comments">
          comment
        </label>
        <textarea
          className="predefinedComments_textarea"
          placeholder={placeholder}
          required={required}
          value={value}
          onChange={(e) => handleCustomChange(e)}
        />
      </div>
    </div>
  );
};

function ClearOptionsModal({
  setOpenOption,
  onSave,
  active,
  isLiti,
  setIsLitiation,
  isRework,
  setIsRework,
  processMode,
  setProcessMode,
  option,
  onOptionChange,
  frequentData,
  claimTypes,
  selectedClaimTypes,
  setSelectedClaimTypes,
  documents,
  onPreviewDoc,
  rework_handleChange,
  err,
  onGenerateNumber,
  suitNumber,
  onAddMissingDoc,
  selectedGaz,
  gazettes,
  missingDocs,
  showOmittedDocs,
  setShowOmittedDocs,
  onRemoveMissing,
  isScreened,
}) {
  const { token } = useContext(ShareContext);
  const { comments: predefinedComments } = useComments(token);
  const [claimTypeError, setClaimTypeError] = useState("");
  const [isScreener, setIsScreener] = useState(false);
  const [gazetteSearch, setGazetteSearch] = useState("");
  const [autocompleteKey, setAutocompleteKey] = useState(0);

  const filteredGazettes = (gazettes || []).filter((gazette) =>
    gazette.title.toLowerCase().includes(gazetteSearch.toLowerCase()),
  );
  useEffect(() => {
    if (option.caseCategoryId == 4 || option.caseCategoryId == 1) {
      setIsScreener(true);
    } else {
      setIsScreener(false);
    }
  }, [option.caseCategoryId]);

  useEffect(() => {
    console.log(isScreened, "isScreened");
  }, [isScreened]);

  const handleSubmit = (e) => {
    if (isLiti == true) {
      e.preventDefault();
      setClaimTypeError("");
      // if (isLiti && selectedClaimTypes.length === 0) {
      //   setClaimTypeError("Please select at least one claim type");
      //   return;
      // }
      if ((suitNumber && suitNumber !== "") || (isScreener && !isScreened)) {
        onSave(e, isScreener);
        setSelectedClaimTypes([]);
      } else {
        onGenerateNumber(e);
      }
    } else {
      setSelectedClaimTypes([]);
      onSave(e);
    }
  };

  const handleClose = () => {
    setShowOmittedDocs(false);
    if (onRemoveMissing && missingDocs) {
      missingDocs.forEach((doc) => onRemoveMissing(doc.id));
    }
    setOpenOption(false);
  };

  return (
    <JisDialogForm title="Clear Options" onClose={handleClose}>
      {/* <form onSubmit={onSave}> */}
      <form
        onSubmit={handleSubmit}
        style={{
          maxWidth: "600px",
        }}
      >
        <div>
          <JisYesNo
            label={`Accept or Reject this Case:  ${active?.referenceNumber}`}
            value={isLiti}
            yes="Accept"
            no="Reject"
            onChange={(e) => setIsLitiation(e)}
            applyButtonStyle={true}
          />
          {isLiti == true && (
            <div>
              {/* <div>

                    <div style={{fontWeight:'bold'}}>Mode of Process</div>
                    <div style={{display:'flex', flexDirection:'row'}}>
                      <div className={`selected ${processMode=='Normal'?'active':''}`} onClick={()=>setProcessMode('Normal')}>Normal</div> 
                      <div  className={`selected ${processMode=='Fast Track'?'active':''}`}  onClick={()=>setProcessMode('Fast Track')}>Fast Track</div> 
                      <div  className={`selected ${processMode=='Urgency'?'active':''}`}  onClick={()=>setProcessMode('Urgency')}>Urgency</div>
                      <div className={`selected ${processMode=='Vacation'?'active':''}`} onClick={()=>setProcessMode('Vacation')}>Vacation Case</div>
                    </div>
                  </div> */}

              <div></div>
              <div className="select-group">
                <label>Process Mode</label>
                <select
                  value={processMode}
                  onChange={(e) => setProcessMode(e.target.value)}
                  required
                >
                  <option value="Normal">Normal </option>
                  <option value="Fast Track">Fast Track </option>
                  <option value="Urgency">Urgency</option>
                  <option value="Vacation">Vacation Case</option>
                </select>
              </div>

              <div className="select-group">
                <label>Division</label>
                <select
                  name="divisionId"
                  value={option.divisionId}
                  onChange={onOptionChange}
                  required
                >
                  <option value="">Select </option>
                  {frequentData.divisions?.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.title}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: "flex" }}>
                <div className="select-group">
                  <label>Classification:</label>
                  <select
                    name="caseCategoryId"
                    value={option.caseCategoryId}
                    onChange={onOptionChange}
                    required
                  >
                    <option value="">Select Classification </option>
                    {frequentData.casecategories?.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.categoryTitle}
                      </option>
                    ))}
                  </select>
                </div>

                { ((!isScreener || isScreened) && //
                  <div className="select-group">
                    <label>Sub-Classification:</label>
                    <select
                      name="subCategoryId"
                      value={option.subCategoryId}
                      onChange={onOptionChange}
                      required
                    >
                      <option value="">Select Sub-Classification </option>
                      {frequentData.subcats
                        ?.filter(
                          (c) => c.caseCategoryId == option.caseCategoryId,
                        )
                        .map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.description}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
              </div>

              {/*  <div >
                        <label>Claim/Offence Type</label>
                        <div style={{display:'flex', flexWrap:'wrap',justifyContent:'space-between'}}>
                           {claimTypes?.map((opt, index) => (
               <div key={index} style={{width:'45%'}}>
                <JisCheckbox label={`${opt.title}`} 
                 onChange={(e)=>onCheck(e,opt.id)} value={selected.has(opt.id)} />

               </div>
                ))} 
                        </div>
                </div> */}
              {(!isScreener || isScreened) && ( 
                <>
                  <div className="input-group">
                    <label>
                      Claim Type <span className="required">Optional</span>
                    </label>
                    <MultiSelectDropdown
                      options={claimTypes}
                      placeholder="Claim Type"
                      value={selectedClaimTypes}
                      onChange={setSelectedClaimTypes}
                    />
                  </div>

                  <div className="input-group">
                    <label>
                      Other Claims{" "}
                      <span className="required">Optional</span>{" "}
                    </label>
                    <div className="input-wrapper">
                      <DynamicIcon iname="group" />
                      <input
                        placeholder="Other claims"
                        name="otherclaims"
                        value={option.otherclaims}
                        onChange={onOptionChange}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          {isLiti == false && (
            <div>
              <JisYesNo
                label={`The mode either Rework or Reject`}
                value={isRework}
                yes="Re-work Document(s)"
                no="Outright Case Reject"
                onChange={(e) => setIsRework(e)}
                applyButtonStyle={true}
              />
              {isRework == true && (
                <div>
                  <p>The documents </p>
                  <div className="table">
                    <div className="section-cases">
                      <span>Case Documents</span>
                    </div>

                    <table className="case-table">
                      <thead>
                        <tr>
                          <th className="col-title">Title</th>
                          {/*   <th>Party</th> */}
                          <th>Filed On</th>

                          <th className="col-action">Preview</th>
                          <th className="col-action">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {documents?.map((opt, index) => (
                          <tr key={opt.id || index}>
                            <td>{opt.title || "N/A"}</td>

                            {/*  <td>{opt.party?.firstName} {opt.party?.lastName??''}</td> */}
                            <td>
                              {Constants.momentDate(opt.createdOn).format(
                                "DD MMM, YYYY",
                              ) || "N/A"}
                            </td>

                            <td>
                              <JisButton
                                variant="plain"
                                onClick={() => onPreviewDoc(opt)}
                              >
                                Preview
                              </JisButton>
                            </td>
                            <td>
                              <div>
                                <div style={{ display: "flex" }}>
                                  <JisCheckbox
                                    label="For Legal Counsel"
                                    value={opt.isLegal}
                                    onChange={(e) =>
                                      rework_handleChange(opt.id, "isLegal", e)
                                    }
                                  />
                                  <JisCheckbox
                                    label="For CFO"
                                    value={opt.isCfo}
                                    onChange={(e) =>
                                      rework_handleChange(opt.id, "isCfo", e)
                                    }
                                  />
                                </div>
                                <div className="input-group">
                                  <div className="input-wrapper">
                                    <input
                                      placeholder="Comment"
                                      value={opt.comment}
                                      onChange={(e) =>
                                        rework_handleChange(
                                          opt.id,
                                          "comment",
                                          e.target.value,
                                        )
                                      }
                                      required={opt.isLegal || opt.isCfo}
                                      disabled={!(opt.isLegal || opt.isCfo)}
                                    />
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ marginTop: 16, marginBottom: 16 }}>
                    <JisCheckbox
                      label="Request Filing of Omitted Documents"
                      value={showOmittedDocs}
                      onChange={(e) => setShowOmittedDocs(e)}
                    />
                  </div>

                  {showOmittedDocs && (
                    <>
                      <div className="select-group">
                        <label>Select Gazette Items:</label>
                        <Autocomplete
                          key={autocompleteKey}
                          options={filteredGazettes}
                          getOptionLabel={(option) => option.title || ""}
                          getOptionKey={(option) => option.id}
                          inputValue={gazetteSearch}
                          onInputChange={(_, newInputValue) =>
                            setGazetteSearch(newInputValue)
                          }
                          onChange={(_event, newValue) => {
                            if (newValue) {
                              onAddMissingDoc(newValue.id);
                              setGazetteSearch("");
                              setAutocompleteKey((k) => k + 1);
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Search gazette items..."
                              size="small"
                              required={missingDocs.length>0?false:showOmittedDocs}
                            />
                          )}
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          noOptionsText="No gazette items found"
                          blurOnSelect
                          size="small"
                        />
                      </div>
                      <div>Added Document ({missingDocs.length})</div>
                      <div style={{ display: "flex" }}>
                        {missingDocs?.map((opt) => (
                          <div
                            key={opt.id}
                            className="selected"
                            style={{ display: "flex", gap: 15 }}
                          >
                            <div style={{ flex: 1 }}>{opt.title}</div>
                            <div>
                              <DynamicIcon
                                iname={"square-x"}
                                size={21}
                                onClick={() => onRemoveMissing(opt.id)}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="input-group">
            <CommentDropdown
              placeholder=" Comment"
              name="comment"
              required={true}
              reasonPlaceHolder={"Reason (select all that apply)"}
              value={option.comment || ""}
              predefinedComments={predefinedComments}
              onChange={(e) =>
                onOptionChange({
                  target: { name: "comment", value: e.target.value },
                })
              }
            />
          </div>
          {/* {err != "" && <div className="error">{err}</div>} */}
          {(err != "" || claimTypeError != "") && (
            <div className="error">{err || claimTypeError}</div>
          )}

          {isLiti == true && (!isScreener || isScreened) && (
            <div style={{ marginBottom: 16 }}>
              {/* <JisButton type="button" onClick={onGenerateNumber}> */}
              <JisButton type="submit">Generate Suit Number </JisButton>{" "}
            </div>
          )}
          {suitNumber != "" && (
            <div style={{ fontSize: 16 }}>
              {" "}
              <strong>{suitNumber}</strong> Will be your permanent suit number
            </div>
          )}
          {(suitNumber != "" ||
            isLiti == false ||
            (isScreener && !isScreened)) && (
            <>
              <div className="groupbtn">
                <JisButton type="submit">Save </JisButton>
                <JisButton
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                >
                  Cancel{" "}
                </JisButton>
              </div>
            </>
          )}
        </div>
      </form>
    </JisDialogForm>
  );
}

ClearOptionsModal.propTypes = {
  setOpenOption: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  active: PropTypes.object,
  isLiti: PropTypes.bool,
  setIsLitiation: PropTypes.func.isRequired,
  isRework: PropTypes.bool,
  setIsRework: PropTypes.func.isRequired,
  processMode: PropTypes.string,
  setProcessMode: PropTypes.func.isRequired,
  option: PropTypes.object,
  onOptionChange: PropTypes.func.isRequired,
  frequentData: PropTypes.object,
  claimTypes: PropTypes.array,
  selectedClaimTypes: PropTypes.array,
  setSelectedClaimTypes: PropTypes.func.isRequired,
  documents: PropTypes.array,
  onPreviewDoc: PropTypes.func,
  rework_handleChange: PropTypes.func,
  err: PropTypes.string,
  onGenerateNumber: PropTypes.func,
  suitNumber: PropTypes.string,
  onAddMissingDoc: PropTypes.func,
  selectedGaz: PropTypes.any,
  gazettes: PropTypes.array,
  missingDocs: PropTypes.array,
  showOmittedDocs: PropTypes.bool,
  setShowOmittedDocs: PropTypes.func,
  onRemoveMissing: PropTypes.func,
};

ClearOptionsModal.defaultProps = {
  active: null,
  isLiti: null,
  isRework: null,
  processMode: "",
  option: {},
  frequentData: {},
  claimTypes: [],
  selectedClaimTypes: [],
  documents: [],
  err: "",
  suitNumber: "",
  selectedGaz: null,
  gazettes: [],
  missingDocs: [],
  showOmittedDocs: false,
};

export default ClearOptionsModal;
